import { http } from '@/interceptors/axiosRequestInterceptor';
import { getImpersonation } from '@/composables/impersonation';

/**
 * Who has the portal installed as an app (Home Screen on iPhone, installed app
 * on Android or desktop). When the portal opens as the installed app it tells
 * the server once (event "open"), again when it comes back to the front after
 * 12 hours, and when the browser reports the install itself ("installed").
 *
 * The device is a random id kept on this device; it says nothing about the
 * device. Nothing is sent from an impersonated session, and a failed ping is
 * never shown: it only means one open is not counted.
 */
const DEVICE_KEY = 'custimoo-device-id';
const AGAIN_AFTER_MS = 12 * 60 * 60 * 1000;

export type AppPlatform = 'ios' | 'android' | 'windows' | 'mac' | 'linux' | 'other';

let started = false;
let lastOpenPing = 0;

const uuid = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const b = new Uint8Array(16);
  crypto.getRandomValues(b);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
};

/** This device's id: made once and kept; a fresh one if storage is unavailable. */
export const deviceId = (): string => {
  try {
    const kept = localStorage.getItem(DEVICE_KEY);
    if (kept && /^[0-9a-f-]{36}$/i.test(kept)) return kept;
    const made = uuid();
    localStorage.setItem(DEVICE_KEY, made);
    return made;
  } catch {
    return uuid();
  }
};

/** Opened as the installed app rather than in a browser tab. */
export const isInstalledApp = (): boolean =>
  ['standalone', 'fullscreen', 'minimal-ui'].some(
    (m) => window.matchMedia?.(`(display-mode: ${m})`).matches === true
  ) || (navigator as unknown as { standalone?: boolean }).standalone === true;

export const appPlatform = (ua = navigator.userAgent): AppPlatform => {
  // iPadOS reports itself as a Mac; a Mac has no touch screen.
  if (/iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) {
    return 'ios';
  }
  if (/Android/i.test(ua)) return 'android';
  if (/Windows/i.test(ua)) return 'windows';
  if (/Macintosh|Mac OS X/i.test(ua)) return 'mac';
  if (/Linux|CrOS/i.test(ua)) return 'linux';
  return 'other';
};

const ping = (event: 'open' | 'installed'): void => {
  if (getImpersonation()) return;
  http
    .post('app-installs/ping', { device_id: deviceId(), event, platform: appPlatform() })
    .catch(() => undefined);
};

const pingOpen = (): void => {
  if (!isInstalledApp()) return;
  const now = Date.now();
  if (lastOpenPing && now - lastOpenPing < AGAIN_AFTER_MS) return;
  lastOpenPing = now;
  ping('open');
};

const onInstalled = (): void => ping('installed');
const onVisible = (): void => {
  if (document.visibilityState === 'visible') pingOpen();
};

/** Call once a user is signed in. */
export const startAppInstallTracking = (): void => {
  if (started || typeof window === 'undefined') return;
  started = true;
  pingOpen();
  window.addEventListener('appinstalled', onInstalled);
  document.addEventListener('visibilitychange', onVisible);
};

/** Tests only. */
export const resetAppInstallTracking = (): void => {
  started = false;
  lastOpenPing = 0;
  window.removeEventListener('appinstalled', onInstalled);
  document.removeEventListener('visibilitychange', onVisible);
};
